/**
 * TickerTape — horizontally-scrolling NSE price strip.
 *
 * Reads /public/ticker-tape (the same endpoint the marketing landing
 * page uses) — unauthenticated, cached upstream for 30 minutes, returns
 * a curated list of large-/mid-cap tickers with last close and day's
 * change %. Refreshes every 5 minutes; market hours computation is
 * client-side so the dot color flips correctly during the trading day.
 *
 * Visual: matches the apex .tape styling — paper background, mono font,
 * ticker · price · ▲/▼ change. Track is duplicated and animated
 * translateX(-50%) for seamless infinite scroll.
 *
 * Renders nothing when the endpoint is unavailable or the list is empty
 * — the layout above it shouldn't break in CI / dev / cold-cache.
 */
import { useEffect, useState } from "react";

interface TickerItem {
  symbol: string;
  close: number;
  change_pct: number;
}

interface TickerTapeResponse {
  as_of: string | null;
  tickers: TickerItem[];
}

const REFRESH_MS = 5 * 60 * 1000;

export function TickerTape() {
  const [tickers, setTickers] = useState<TickerItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)
      ?.replace(/\/$/, "");
    if (!apiBase) return;

    const fetchTape = async () => {
      try {
        const res = await fetch(`${apiBase}/public/ticker-tape`);
        if (!res.ok) return;
        const data = (await res.json()) as TickerTapeResponse;
        if (!cancelled && data.tickers) setTickers(data.tickers);
      } catch {
        // Silent — the tape is non-critical chrome.
      }
    };

    void fetchTape();
    const id = setInterval(fetchTape, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (tickers.length === 0) return null;

  // Duplicate the list so the animation loops seamlessly when the
  // first copy scrolls off-screen. The same item key suffix is used
  // to satisfy React's reconciliation across the duplicate.
  const items = [...tickers, ...tickers];

  return (
    <div
      style={{
        background: "var(--paper)",
        borderBottom: "1px solid var(--line)",
        overflow: "hidden",
        fontFamily: "var(--mono)",
        fontSize: 12.5,
        color: "var(--ink-mid)",
      }}
      aria-label="NSE ticker tape"
    >
      <div
        style={{
          display: "flex",
          gap: 42,
          padding: "8px 0",
          whiteSpace: "nowrap",
          width: "max-content",
          animation: "ssTickerScroll 80s linear infinite",
        }}
      >
        {items.map((t, i) => {
          const up = t.change_pct >= 0;
          return (
            <span
              key={`${t.symbol}-${i}`}
              style={{
                display: "inline-flex",
                gap: 10,
                alignItems: "center",
                paddingInline: 0,
              }}
            >
              <span
                style={{
                  color: "var(--ink)",
                  fontWeight: 500,
                  letterSpacing: ".04em",
                }}
              >
                {t.symbol}
              </span>
              <span style={{ color: "var(--ink)" }}>
                ₹{t.close.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </span>
              <span
                style={{
                  color: up ? "var(--gain-text)" : "var(--loss-text)",
                  fontWeight: 500,
                }}
              >
                <span style={{ fontSize: 9, marginRight: 2 }}>
                  {up ? "▲" : "▼"}
                </span>
                {up ? "+" : ""}
                {t.change_pct.toFixed(2)}%
              </span>
            </span>
          );
        })}
      </div>
      <style>{`
        @keyframes ssTickerScroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          [aria-label="NSE ticker tape"] > div {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
